import React, { useEffect, useState } from "react";

import { usePlataform } from "@/pages/platforms/platform.hooks";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";

interface PlatformColorsProps {
  onPrimaryColorChange?: (color: string) => void;
  onSecondaryColorChange?: (color: string) => void;
  onBackgroundColorChange?: (color: string) => void;
  selectedPrimaryColor?: string;
  selectedSecondaryColor?: string;
  selectedBackgroundColor?: string;
}

// Paleta extensa de cores predefinidas
const COLOR_PALETTE = [
  // Azuis
  { name: "Azul Royal", value: "#1e40af" },
  { name: "Azul Céu", value: "#0ea5e9" },
  { name: "Azul Marinho", value: "#1e3a8a" },
  { name: "Azul Claro", value: "#3b82f6" },
  { name: "Azul Turquesa", value: "#0891b2" },
  { name: "Azul Petróleo", value: "#0f766e" },
  { name: "Azul Índigo", value: "#4338ca" },
  { name: "Azul Cobalto", value: "#1d4ed8" },
  { name: "Azul Celeste", value: "#87ceeb" },
  { name: "Azul Aço", value: "#4682b4" },
  { name: "Azul Meia-Noite", value: "#191970" },
  { name: "Azul Dodger", value: "#1e90ff" },
  
  // Roxos/Violetas
  { name: "Roxo", value: "#6d4f97" },
  { name: "Violeta", value: "#8b5cf6" },
  { name: "Roxo Escuro", value: "#5b21b6" },
  { name: "Lavanda", value: "#a78bfa" },
  { name: "Roxo Profundo", value: "#7c3aed" },
  { name: "Magenta", value: "#c026d3" },
  { name: "Roxo Claro", value: "#c084fc" },
  { name: "Púrpura", value: "#9333ea" },
  { name: "Orquídea", value: "#da70d6" },
  { name: "Ameixa", value: "#dda0dd" },
  { name: "Roxo Médio", value: "#9370db" },
  { name: "Violeta Escuro", value: "#9400d3" },
  
  // Verdes
  { name: "Verde", value: "#059669" },
  { name: "Verde Esmeralda", value: "#10b981" },
  { name: "Verde Escuro", value: "#047857" },
  { name: "Verde Claro", value: "#34d399" },
  { name: "Verde Lima", value: "#65a30d" },
  { name: "Verde Menta", value: "#6ee7b7" },
  { name: "Verde Floresta", value: "#166534" },
  { name: "Verde Oliva", value: "#84cc16" },
  { name: "Verde Mar", value: "#2e8b57" },
  { name: "Verde Primavera", value: "#00ff7f" },
  { name: "Verde Musgo", value: "#8fbc8f" },
  { name: "Verde Grama", value: "#7cfc00" },
  
  // Vermelhos
  { name: "Vermelho", value: "#dc2626" },
  { name: "Rosa", value: "#e11d48" },
  { name: "Vermelho Escuro", value: "#991b1b" },
  { name: "Rosa Claro", value: "#f43f5e" },
  { name: "Vermelho Carmim", value: "#b91c1c" },
  { name: "Rosa Quente", value: "#ec4899" },
  { name: "Vermelho Escarlate", value: "#ef4444" },
  { name: "Rosa Suave", value: "#f9a8d4" },
  { name: "Vermelho Tomate", value: "#ff6347" },
  { name: "Rosa Choque", value: "#ff1493" },
  { name: "Vermelho Indiano", value: "#cd5c5c" },
  { name: "Rosa Pêssego", value: "#ffc0cb" },
  
  // Laranjas/Amarelos
  { name: "Laranja", value: "#ea580c" },
  { name: "Amarelo", value: "#d97706" },
  { name: "Laranja Escuro", value: "#c2410c" },
  { name: "Amarelo Claro", value: "#f59e0b" },
  { name: "Laranja Queimado", value: "#ea580c" },
  { name: "Amarelo Dourado", value: "#eab308" },
  { name: "Laranja Suave", value: "#fb923c" },
  { name: "Amarelo Limão", value: "#facc15" },
  { name: "Laranja Coral", value: "#ff7f50" },
  { name: "Amarelo Ouro", value: "#ffd700" },
  { name: "Laranja Escuro", value: "#ff8c00" },
  { name: "Amarelo Canário", value: "#ffff00" },
  
  // Cinzas/Neutros
  { name: "Cinza", value: "#6b7280" },
  { name: "Cinza Escuro", value: "#374151" },
  { name: "Preto", value: "#111827" },
  { name: "Cinza Claro", value: "#9ca3af" },
  { name: "Cinza Médio", value: "#4b5563" },
  { name: "Cinza Aço", value: "#64748b" },
  { name: "Cinza Pérola", value: "#d1d5db" },
  { name: "Branco Quebrado", value: "#f3f4f6" },
  { name: "Cinza Ardósia", value: "#708090" },
  { name: "Branco", value: "#ffffff" },
  { name: "Cinza Escuro", value: "#2d3748" },
  { name: "Cinza Claro", value: "#e2e8f0" },
  
  // Cores especiais
  { name: "Dourado", value: "#f59e0b" },
  { name: "Prata", value: "#9ca3af" },
  { name: "Bronze", value: "#cd7f32" },
  { name: "Cobre", value: "#b87333" },
  { name: "Coral", value: "#ff7f50" },
  { name: "Salmão", value: "#fa8072" },
  { name: "Pêssego", value: "#ffcba4" },
  { name: "Creme", value: "#f5f5dc" },
  { name: "Marrom", value: "#8b4513" },
  { name: "Bege", value: "#f5f5dc" },
  { name: "Champagne", value: "#f7e7ce" },
  { name: "Terracota", value: "#e2725b" },
];

function ColorPicker({
  label,
  color,
  onChange,
  description,
}: {
  label: string;
  color: string;
  onChange: (color: string) => void;
  description?: string;
}) {
  const [showPalette, setShowPalette] = useState(false);
  const [activeTab, setActiveTab] = useState<"preset" | "custom">("preset");
  const [tempColor, setTempColor] = useState(color);

  // Atualizar tempColor quando color mudar
  React.useEffect(() => {
    setTempColor(color);
  }, [color]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <div className="font-['Poppins',_sans-serif] font-medium text-sm leading-[20px]">
          {label}
        </div>
        {description && (
          <div className="font-['Poppins',_sans-serif] text-[#787878] text-xs leading-[16px]">
            {description}
          </div>
        )}
      </div>

      {/* Preview da cor atual */}
      <div className="flex items-center gap-3">
        <div
          className="w-12 h-12 rounded-lg border-2 border-[#353535] cursor-pointer transition-all duration-200 hover:scale-105 hover:border-[#404040] shadow-lg"
          style={{ backgroundColor: color }}
          onClick={() => setShowPalette(!showPalette)}
          title="Clique para escolher uma cor"
        />
        <div className="flex flex-col">
          <span className="text-sm font-medium text-white">{color.toUpperCase()}</span>
          <span className="text-xs text-[#787878]">Clique para alterar</span>
        </div>
      </div>

      {/* Seletor de cores com abas */}
      {showPalette && (
        <div className="bg-[#1a1a1a] rounded-lg border border-[#353535] p-4 max-h-96 overflow-hidden">
          {/* Abas */}
          <div className="flex mb-4 border-b border-[#353535]">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveTab("preset");
              }}
              className={`px-4 py-2 text-sm font-medium transition-all duration-200 ${
                activeTab === "preset"
                  ? "text-white border-b-2 border-purple-500"
                  : "text-[#787878] hover:text-white"
              }`}
            >
              Cores Predefinidas
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveTab("custom");
              }}
              className={`px-4 py-2 text-sm font-medium transition-all duration-200 ${
                activeTab === "custom"
                  ? "text-white border-b-2 border-purple-500"
                  : "text-[#787878] hover:text-white"
              }`}
            >
              Personalizar
            </button>
          </div>

          {/* Conteúdo das abas */}
          {activeTab === "preset" ? (
            /* Aba de cores predefinidas */
            <div className="grid grid-cols-8 gap-2 max-h-64 overflow-y-auto">
              {COLOR_PALETTE.map((colorOption) => (
                <button
                  key={colorOption.value}
                  className="w-8 h-8 rounded-md border-2 transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-purple-500/50 shadow-sm"
                  style={{
                    backgroundColor: colorOption.value,
                    borderColor: color === colorOption.value ? "#ffffff" : "rgba(255,255,255,0.2)",
                  }}
                  onClick={() => {
                    onChange(colorOption.value);
                    setShowPalette(false);
                  }}
                  title={`${colorOption.name} - ${colorOption.value}`}
                />
              ))}
            </div>
          ) : (
            /* Aba de personalização */
            <div className="space-y-4">
              {/* Seletor de cor nativo */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-[#787878]">Seletor de cor:</span>
                <input
                  type="color"
                  value={tempColor}
                  onChange={(e) => {
                    e.stopPropagation();
                    setTempColor(e.target.value);
                  }}
                  className="w-12 h-12 rounded border border-[#353535] cursor-pointer"
                  title="Seletor de cor personalizada"
                />
              </div>

              {/* Campo de texto para código hexadecimal */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-[#787878]">Código hexadecimal:</span>
                <input
                  type="text"
                  value={tempColor.toUpperCase()}
                  onChange={(e) => {
                    e.stopPropagation();
                    const value = e.target.value;
                    if (/^#[0-9A-F]{6}$/i.test(value)) {
                      setTempColor(value);
                    }
                  }}
                  className="w-24 h-8 px-3 bg-[#191919] border border-[#353535] rounded-md text-white text-sm font-mono
                             focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/30
                             hover:border-[#404040] transition-all duration-200"
                  placeholder="#6d4f97"
                  maxLength={7}
                />
              </div>

              {/* Preview da cor personalizada */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-[#787878]">Preview:</span>
                <div
                  className="w-16 h-16 rounded-lg border-2 border-[#353535]"
                  style={{ backgroundColor: tempColor }}
                />
                <span className="text-sm text-white font-mono">{tempColor.toUpperCase()}</span>
              </div>

              {/* Botão para aplicar a cor */}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setTempColor(color);
                  }}
                  className="px-3 py-1 text-xs bg-[#2a2a2a] text-[#787878] rounded hover:bg-[#3a3a3a] hover:text-white transition-all duration-200"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange(tempColor);
                    setShowPalette(false);
                  }}
                  className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 transition-all duration-200"
                >
                  Aplicar
                </button>
              </div>
            </div>
          )}

          {/* Informações */}
          <div className="mt-4 pt-4 border-t border-[#353535] text-xs text-[#787878] text-center">
            {activeTab === "preset" 
              ? `${COLOR_PALETTE.length} cores predefinidas disponíveis` 
              : "Personalize sua cor usando o seletor ou código hexadecimal"
            }
          </div>
        </div>
      )}
    </div>
  );
}

export function PlatformColors({
  onPrimaryColorChange,
  onSecondaryColorChange,
  onBackgroundColorChange,
  selectedPrimaryColor,
  selectedSecondaryColor,
  selectedBackgroundColor,
}: PlatformColorsProps) {
  const {
    primaryColor: contextPrimaryColor,
    secondaryColor: contextSecondaryColor,
    setPrimaryColor: setContextPrimaryColor,
    setSecondaryColor: setContextSecondaryColor,
  } = usePlataform();

  const [primaryColor, setPrimaryColor] = useState(
    selectedPrimaryColor || contextPrimaryColor
  );
  const [secondaryColor, setSecondaryColor] = useState(
    selectedSecondaryColor || contextSecondaryColor
  );
  const [backgroundColor, setBackgroundColor] = useState(
    selectedBackgroundColor || "#ffffff"
  );

  // Sincronizar com o contexto quando as cores mudarem
  useEffect(() => {
    if (selectedPrimaryColor && selectedPrimaryColor !== primaryColor) {
      setPrimaryColor(selectedPrimaryColor);
    }
  }, [selectedPrimaryColor, primaryColor]);

  useEffect(() => {
    if (selectedSecondaryColor && selectedSecondaryColor !== secondaryColor) {
      setSecondaryColor(selectedSecondaryColor);
    }
  }, [selectedSecondaryColor, secondaryColor]);

  useEffect(() => {
    if (selectedBackgroundColor && selectedBackgroundColor !== backgroundColor) {
      setBackgroundColor(selectedBackgroundColor);
    }
  }, [selectedBackgroundColor, backgroundColor]);

  const handlePrimaryColorChange = (color: string) => {
    setPrimaryColor(color);
    setContextPrimaryColor(color); // Atualizar o contexto global
    onPrimaryColorChange?.(color);
  };

  const handleSecondaryColorChange = (color: string) => {
    setSecondaryColor(color);
    setContextSecondaryColor(color); // Atualizar o contexto global
    onSecondaryColorChange?.(color);
  };

  const handleBackgroundColorChange = (color: string) => {
    setBackgroundColor(color);
    onBackgroundColorChange?.(color);
  };

  // Determinar se a cor é clara para ajustar o texto
  const isLightColor = (color: string) => {
    const hex = color.replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Coluna 1: Cor Primária */}
      <div className="space-y-4">
        <ColorPicker
          label="Cor primária"
          description="Cor principal da interface e elementos de destaque"
          color={primaryColor}
          onChange={handlePrimaryColorChange}
        />
      </div>

      {/* Coluna 2: Cor Secundária */}
      <div className="space-y-4">
        <ColorPicker
          label="Cor secundária"
          description="Cor de fundo e elementos secundários"
          color={secondaryColor}
          onChange={handleSecondaryColorChange}
        />
      </div>

      {/* Coluna 3: Cor de Fundo */}
      <div className="space-y-4">
        <ColorPicker
          label="Cor de fundo"
          description="Cor de fundo da página"
          color={backgroundColor}
          onChange={handleBackgroundColorChange}
        />
      </div>

      {/* Coluna 4: Preview */}
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Preview da combinação</CardTitle>
          </CardHeader>

          <CardContent>
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-8 h-8 rounded-full border border-white"
                style={{ backgroundColor: primaryColor }}
              />
              <div
                className="w-8 h-8 rounded-full border border-white"
                style={{ backgroundColor: secondaryColor }}
              />
              <div
                className="w-8 h-8 rounded-full border border-white"
                style={{ backgroundColor: backgroundColor }}
              />
              <div className="text-[#8b8b8b] text-xs">Cores selecionadas</div>
            </div>

            {/* Preview de página */}
            <div 
              className="p-4 rounded-lg border transition-colors"
              style={{
                backgroundColor: backgroundColor,
                borderColor: primaryColor,
              }}
            >
              <div className="space-y-2">
                <div
                  className="text-lg font-bold"
                  style={{
                    color: primaryColor,
                  }}
                >
                  Título da Página
                </div>
                <div
                  className="text-sm"
                  style={{
                    color: isLightColor(backgroundColor) ? "#000" : "#fff",
                  }}
                >
                  Texto de exemplo na cor de fundo
                </div>
                <button
                  className="px-3 py-1 rounded text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: primaryColor,
                    color: isLightColor(primaryColor) ? "#000" : "#fff",
                  }}
                >
                  Botão
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
